import { RoleFunction } from '@eagle/common';
import { MenuItem, Select, Stack, styled, Typography } from '@ethings/ui-toolkit';
import { ButtonBase, useTheme } from '@mui/material';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, IconWithTooltip, ResponsiveText, useBoolFlag } from '../../components';
import { useHasAuthorization } from '../../util';
import { useContractListContext } from './contract-list-provider';
import { ContractManagementManuallyManageMenu } from './contract-management-manually-manage-menu';
import { ContractUnavailabilityReason, SelectablePlan, ThingWithContractData } from './contract-management.types';
import { canSelectDataSubscriptionPlan, deserializeSelectValue, formatDataSubscriptionPlanWithTerm, formatPrice, getSelectablePlans, serializeSelectValue } from './contract-management.utils';

const SYSTEM_ADMIN_ROLE = [RoleFunction.SYSTEM_ADMINISTRATOR];
const DATA_SUBSCRIPTION_ADMINISTRATOR_ROLE = [RoleFunction.DATA_SUBSCRIPTION_ADMINISTRATOR];

interface ContractListPlanSelectProps {
  item: ThingWithContractData;
}

const StyledPlanSelect = styled(Select)({
  overflow: 'hidden',
});

export const ContractListPlanSelect: FC<ContractListPlanSelectProps> = ({ item }) => {
  const { hasAuthorization } = useHasAuthorization();
  const hasSysAdminPermissions = hasAuthorization(SYSTEM_ADMIN_ROLE);
  const hasDataSubscriptionAdminPermissions = hasAuthorization(DATA_SUBSCRIPTION_ADMINISTRATOR_ROLE);
  const hasAdminContractManualManagementFlag = useBoolFlag('admin-contract-management-manual-contract-management-feature');
  const hasContractManualManagementPermission = (hasSysAdminPermissions || hasDataSubscriptionAdminPermissions) && hasAdminContractManualManagementFlag;
  const { state: { selectedCurrency, cartItems, checkout }, addCartItem, removeCartItem } = useContractListContext();
  const { t, i18n } = useTranslation(['common', 'data-subscription']);
  const theme = useTheme();

  const itemInCart = cartItems.find((cartItem) => cartItem.thing._id === item._id);

  const getValue = (): string => {
    if (!itemInCart) {
      return '';
    }
    return serializeSelectValue({ dataSubscriptionPlanId: itemInCart.dataSubscriptionPlan.dataSubscriptionPlanId, term: itemInCart.term });
  };

  const removeFromCart = (): void => {
    removeCartItem(item._id);
  };

  const selectablePlans = getSelectablePlans(item.thingTypeWithDataSubscriptions?.dataSubscriptionPlans ?? [], selectedCurrency);
  const canSelect = canSelectDataSubscriptionPlan(item.contract.dataSubscription, selectablePlans);
  const emptyMessage = canSelect.result ? t('common:page.contract-management.select-a-plan.labels') : t('common:page.contract-management.no-plan-available.labels');

  const findOption = (value: string): SelectablePlan => {
    const deserializedValue = deserializeSelectValue(value);
    const option = selectablePlans.find((option) => option.plan.dataSubscriptionPlanId === deserializedValue.dataSubscriptionPlanId && option.term === deserializedValue.term);
    if (!option) {
      throw new Error('Missing option.');
    }
    return option;
  };

  return (
    <Stack alignItems="center" direction="row" spacing={1}>
      <StyledPlanSelect
        data-testid="plan-select"
        disabled={!canSelect.result || checkout.status !== 'idle'}
        SelectDisplayProps={{ 'data-testid': 'plan-select-combobox' } as React.HTMLAttributes<HTMLDivElement>}
        displayEmpty
        fullWidth
        onChange={(e) => {
          if (e.target.value === '') {
            return removeFromCart();
          }
          const option = findOption(e.target.value as string);
          addCartItem({
            thing: item.thing,
            dataSubscriptionPlan: option.plan,
            term: option.term,
          });
        }}
        renderValue={(value) => {
          if (value === '') {
            return emptyMessage;
          }
          const option = findOption(value as string);
          return formatDataSubscriptionPlanWithTerm(i18n, option.plan, option.term);
        }}
        size="small"
        value={getValue()}
      >
        <MenuItem value=""><em>{emptyMessage}</em></MenuItem>
        {selectablePlans.map((selectablePlan) => {
          const planText = formatDataSubscriptionPlanWithTerm(i18n, selectablePlan.plan, selectablePlan.term);
          const priceText = formatPrice(i18n, selectedCurrency, selectablePlan.price);

          return (
            <MenuItem
              key={`${selectablePlan.plan.dataSubscriptionPlanId}-${selectablePlan.term}`}
              data-testid={`${selectablePlan.plan.dataSubscriptionPlanId}-${selectablePlan.term}`}
              value={serializeSelectValue({ dataSubscriptionPlanId: selectablePlan.plan.dataSubscriptionPlanId, term: selectablePlan.term })}
            >
              <Stack width="100%" minWidth={0}>
                <ResponsiveText
                  responsiveOptions={{
                    minFontSize: '0.8rem',
                    maxFontSize: '1rem',
                    textLengthThreshold: 30,
                  }}
                  style={{ wordBreak: 'break-word' }}
                >
                  {planText}
                </ResponsiveText>
                <Typography variant="body2" color="text.secondary">{priceText}</Typography>
              </Stack>
            </MenuItem>
          );
        })}
      </StyledPlanSelect>
      {!canSelect.result && (
        <IconWithTooltip arrow data-testid="plan-select-warning" icon={<Icon icon="Warning" color="warning" />} title={t(getWarningKey(item, canSelect.reason))} />
      )}
      {itemInCart && (
        <ButtonBase data-testid="plan-select-remove-button" disabled={checkout.status !== 'idle'} disableRipple onClick={removeFromCart} sx={{ color: theme.palette.text.secondary }}>
          <Icon icon="Refresh" />
        </ButtonBase>
      )}
      {hasContractManualManagementPermission && (
        <ContractManagementManuallyManageMenu item={item} selectablePlans={selectablePlans} />
      )}
    </Stack>
  );
};

const getWarningKey = (item: ThingWithContractData, reason: ContractUnavailabilityReason): string => {
  switch (reason) {
    case 'no-contract':
      return 'common:page.contract-management.no-contract-for-vehicle.labels';
    case 'already-in-cart':
      return 'common:page.contract-management.plan-already-in-another-cart.labels';
    case 'no-plans':
      return 'common:page.contract-management.plan-unavailable-for-selected-currency.labels';
    case 'non-owner':
      return item.contract.dataSubscription?.lastDataSubscription?.finish
        ? 'common:page.contract-management.expired-contract-non-owner.labels'
        : 'common:page.contract-management.contract-non-owner.labels';
  }
};
